import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Building2, ShieldCheck, Crown, Calendar, ChevronDown } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Public branded landing page for an Organization.
 * URL: /o/:slug (public, no auth required)
 * Shareable — every seat is displayed as a mini practitioner card with
 * an inline booking drawer so visitors book without leaving /o/:slug.
 */
export default function OrgPublicPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [openSeat, setOpenSeat] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/public/orgs/${slug}`)
      .then(r => setData(r.data))
      .catch(e => { if (e.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-amber-200/60">Loading…</div>;
  if (notFound) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-neutral-950 text-amber-100 p-8" data-testid="org-public-notfound">
      <h1 className="text-2xl font-serif mb-2">Organization not found</h1>
      <Link to="/" className="text-amber-400 underline">Back home</Link>
    </div>
  );

  const { org, seats } = data;

  return (
    <div className="min-h-screen bg-neutral-950 text-amber-100" data-testid="org-public-page">
      {/* Header / hero */}
      <div
        className="relative"
        style={{
          backgroundImage: org.coverUrl
            ? `linear-gradient(to bottom, rgba(10,7,5,0.4), rgba(10,7,5,1)), url(${org.coverUrl})`
            : "linear-gradient(160deg, rgba(120,60,20,0.4), rgba(10,7,5,1))",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-5xl mx-auto px-6 py-20 md:py-28">
          <div className="flex items-center gap-4 mb-4">
            {org.logoUrl ? (
              <img src={org.logoUrl} alt={org.name} className="w-20 h-20 rounded-full border-2 border-amber-500/40 object-cover" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-amber-950/80 border-2 border-amber-500/40 flex items-center justify-center">
                <Building2 className="h-10 w-10 text-amber-400" />
              </div>
            )}
            <div>
              <div className="text-xs uppercase tracking-widest text-amber-400 mb-1">Spiritual Organization</div>
              <h1 className="text-3xl sm:text-5xl font-serif text-amber-50" data-testid="org-name">{org.name}</h1>
              <div className="text-sm text-amber-200/60 mt-1">/o/{org.slug} · {org.seatCount} practitioner{org.seatCount === 1 ? "" : "s"}</div>
            </div>
          </div>
          {org.description && (
            <p className="text-lg text-amber-100/85 leading-relaxed max-w-2xl mt-6" data-testid="org-description">
              {org.description}
            </p>
          )}
        </div>
      </div>

      {/* Seats grid */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        <h2 className="text-2xl font-serif text-amber-100 mb-6">Practitioners</h2>
        {seats.length === 0 ? (
          <div className="text-amber-200/60 text-sm py-8 text-center">No practitioners have joined yet.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="seat-grid">
            {seats.map(s => {
              const expanded = openSeat === s.userId;
              const hasServices = (s.services || []).length > 0;
              return (
                <div
                  key={s.userId}
                  className="rounded-lg border border-neutral-800 bg-neutral-900/40 hover:border-amber-500/40 transition p-5 flex flex-col"
                  data-testid={`seat-${s.userId}`}
                >
                  <Link to={`/profile/${s.userId}`} className="flex items-center gap-3 mb-3">
                    {s.avatarUrl ? (
                      <img src={s.avatarUrl} alt="" className="w-14 h-14 rounded-full object-cover" />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-amber-950/60 flex items-center justify-center text-xl font-serif text-amber-200">
                        {(s.name || "?")[0].toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-amber-100 font-serif truncate flex items-center gap-1">
                        {s.name}
                        {s.verified && <ShieldCheck className="h-4 w-4 text-emerald-400 flex-shrink-0" />}
                        {s.elder && <Crown className="h-4 w-4 text-amber-500 flex-shrink-0" />}
                      </div>
                      {s.tradition && <div className="text-xs text-amber-200/60 truncate">{s.tradition}</div>}
                    </div>
                  </Link>
                  {s.bio && <p className="text-sm text-amber-100/70 line-clamp-3 flex-1 mb-3">{s.bio}</p>}

                  {hasServices && (
                    <>
                      <button
                        onClick={() => setOpenSeat(expanded ? null : s.userId)}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-md border border-amber-500/40 text-amber-200 hover:bg-amber-950/30 text-xs uppercase tracking-wider"
                        data-testid={`book-toggle-${s.userId}`}
                      >
                        <span className="flex items-center gap-2">
                          <Calendar className="h-3.5 w-3.5" /> Book a service
                        </span>
                        <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
                      </button>
                      {expanded && (
                        <div className="mt-3 space-y-2" data-testid={`services-${s.userId}`}>
                          {s.services.map(svc => (
                            <Link
                              key={svc.id}
                              to={`/profile/${s.userId}#service-${svc.id}`}
                              className="block px-3 py-2 rounded-md bg-neutral-900/80 border border-neutral-800 hover:border-amber-500/30"
                              data-testid={`service-${svc.id}`}
                            >
                              <div className="flex justify-between items-center">
                                <span className="text-amber-100 text-sm line-clamp-1">{svc.title}</span>
                                <span className="text-amber-50 text-sm font-serif ml-2 whitespace-nowrap">${(svc.price || 0).toFixed(2)}</span>
                              </div>
                              {svc.duration && (
                                <div className="text-xs text-amber-200/50 mt-0.5">{svc.duration} min</div>
                              )}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  {!hasServices && (
                    <Link to={`/profile/${s.userId}`} className="text-xs text-amber-400 mt-auto uppercase tracking-wider" data-testid={`view-profile-${s.userId}`}>
                      View profile →
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <footer className="border-t border-neutral-900 py-8 text-center text-xs text-amber-200/40">
        <Link to="/" className="hover:text-amber-200">Powered by Cauldron</Link>
      </footer>
    </div>
  );
}
